import { FC, KeyboardEvent, CSSProperties, ForwardRefRenderFunction, ForwardedRef } from 'react';
import { styled } from 'styled-components';
import { useUpdateRef } from '@/hooks/gHooks';
import { insertTextAtCursor } from '@/utils';
import { validMsg } from '@/utils/valid';
import { useLang } from '@/locales/useLang';
import { callFromExternal } from '@/utils';

interface IInputProps {
  onChange: (content: string) => void;
  onSend?: (content: string) => void;
  style?: CSSProperties;
  maxWord?: number;
  isFullScreen?: boolean;
  onFocus?: (isFocus: boolean) => void;
}

export interface IInputRef {
  inputRef: HTMLDivElement;
  resetContent: () => void;
}

const InputWrap = styled.div<{ $isFullScreen: boolean; $color: string; $fontSize: string }>`
  width: ${(prpos) => (prpos.$isFullScreen ? '670rem' : '100%')};
  height: 100%;
  position: ${(prpos) => (prpos.$isFullScreen ? 'fixed' : 'static')};
  word-break: break-all;

  overflow: auto;
  font-size: ${(props) => props.$fontSize};
  left: 0;
  right: 0;
  margin: 0 auto;
  color: ${(props) => props.$color};

  ${(props) => {
    if (!props.$isFullScreen) {
      return 'max-height: 230rem; top: 176rem';
    }
  }}
`;

const Input = forwardRef((props: IInputProps, ref: ForwardedRef<IInputRef>) => {
  const $t = useLang();

  const placeHolderText = $t('chat_session_status_12');

  const [placeHolder, setPlaceHolder] = useState(placeHolderText);
  const [contentText, updateContentText] = useUpdateRef('');

  const inputRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        document.execCommand('insertLineBreak');
        e.preventDefault();
      } else {
        e.preventDefault();
        if (!validMsg(String(inputRef.current?.innerHTML))) {
          props.onSend?.('');
          return;
        }
        document.execCommand('insertLineBreak');
        props.onSend?.(String(inputRef.current));
      }
    }
  };

  /**
   * 重置输入框消息
   */
  const resetContent = () => {
    inputRef.current && (inputRef.current.innerHTML = '');
  };

  useImperativeHandle<IInputRef, any>(ref, () => ({
    inputRef: inputRef.current,
    contentText,
    resetContent,
  }));

  const handleFocus = () => {
    if (!contentText.current) {
      setTimeout(() => {
        const selection = window.getSelection();
        const range = document.createRange();

        range.setStart(inputRef.current!, 0);
        range.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(range);

        setPlaceHolder('');
      }, 0);
    }

    props.onFocus?.(true);
  };

  const handleBlur = () => {
    if (!contentText.current) {
      setPlaceHolder(placeHolderText);
    }
    props.onFocus?.(false);
  };

  return (
    <InputWrap
      onPaste={(e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        insertTextAtCursor(text);
      }}
      ref={inputRef}
      $color={callFromExternal() && !inputRef.current?.innerText ? 'rgba(69, 72, 76, 1) !important' : placeHolder ? '#EBEBEB' : '#000'}
      $fontSize={placeHolder ? '12rem' : '14rem'}
      $isFullScreen={props.isFullScreen ?? false}
      contentEditable
      dangerouslySetInnerHTML={{ __html: placeHolder }}
      onFocus={() => handleFocus()}
      onBlur={() => handleBlur()}
      onInput={() => {}}
      onKeyUp={(e) => {
        setTimeout(() => {
          const content = inputRef.current?.innerHTML;
          contentText.current = String(inputRef.current?.textContent);
          // updateContentText();
          props.onChange(content!);
        }, 0);
      }}
      onKeyDown={(e) => {
        const c = inputRef.current?.textContent;
        contentText.current = String(c);
        // setContentText(String(c));

        if (c && c.length > props.maxWord!) {
          inputRef.current.textContent = c.substring(0, props.maxWord);

          const range = document.createRange();
          const selection = window.getSelection();

          range.selectNodeContents(inputRef.current!);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);

          return;
        }
        handleKeyDown(e);
      }}
    ></InputWrap>
  );
});

export default Input;

export const TextCount: FC<{ count: string; maxWord: number }> = ({ count, maxWord }) => {
  const showCount = useMemo(() => {
    const content = count
      .replace(/<br\s*\/?>/gi, '')
      .replace(/&nbsp;/gi, '')
      .trim();

    return content.length;
  }, [count]);

  return (
    <div className={`text-gray text-12`}>
      {showCount || 0}/{maxWord}
    </div>
  );
};
